import { useState, useEffect } from "react";
import { X, Search } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";
const SPIERGROEPEN = ["Benen","Billen","Borst","Rug","Schouders","Biceps","Triceps","Core","Full Body","Kuiten","Nek","Anders"];

/**
 * ExercisePickerModal
 * Props:
 *   onSelect(oefening) - wordt aangeroepen met het gekozen oefening-object
 *   onClose()          - sluit de modal
 *   token              - JWT token (optioneel, voor nieuwe oefeningen toevoegen)
 */
export default function ExercisePickerModal({ onSelect, onClose, token }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("");
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API}/api/exercises`, { headers })
      .then(r => r.json())
      .then(d => { setExercises(d.exercises || d || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const filtered = exercises.filter(e =>
    (!query || e.naam?.toLowerCase().includes(query.toLowerCase())) &&
    (!filter || e.spiergroep === filter)
  );

  // Groepeer per spiergroep voor overzicht
  const grouped = filtered.reduce((acc, ex) => {
    const sg = ex.spiergroep || "Anders";
    if (!acc[sg]) acc[sg] = [];
    acc[sg].push(ex);
    return acc;
  }, {});

  return (
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        data-testid="exercise-picker-modal"
        className="w-full max-w-md max-h-[85vh] flex flex-col rounded-2xl border border-white/10 overflow-hidden"
        style={{ background: "#111" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8 shrink-0">
          <h3 className="text-white font-black text-sm">Oefening kiezen</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
            <X size={15} />
          </button>
        </div>

        {/* Zoekbalk */}
        <div className="px-4 pt-3 pb-2 shrink-0 space-y-2">
          <div className="flex items-center gap-2 bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2.5">
            <Search size={13} className="text-zinc-500 shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Zoek oefening..."
              data-testid="exercise-picker-search"
              className="bg-transparent flex-1 text-sm text-white outline-none placeholder-zinc-600"
            />
            {query && (
              <button onClick={() => setQuery("")} className="text-zinc-600 hover:text-zinc-400">
                <X size={12} />
              </button>
            )}
          </div>

          {/* Spiergroep filter chips */}
          <div className="flex gap-1.5 flex-wrap">
            <button
              onClick={() => setFilter("")}
              className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors"
              style={!filter ? { background: GOLD, color: "#000" } : { background: "#1a1a1a", color: "#71717a" }}
            >
              Alles
            </button>
            {SPIERGROEPEN.map(s => (
              <button
                key={s}
                onClick={() => setFilter(filter === s ? "" : s)}
                className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors"
                style={filter === s ? { background: GOLD, color: "#000" } : { background: "#1a1a1a", color: "#71717a" }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Oefening lijst */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {loading && (
            <div className="flex items-center justify-center py-10">
              <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin"
                style={{ borderColor: GOLD, borderTopColor: "transparent" }} />
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <p className="text-zinc-600 text-sm text-center py-8">Geen oefeningen gevonden</p>
          )}

          {!loading && !filter && !query ? (
            // Gegroepeerde weergave als geen filter/query actief
            Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([sg, oefs]) => (
              <div key={sg} className="mb-3">
                <p className="text-zinc-600 text-[10px] font-black uppercase tracking-widest px-1 py-1.5">{sg}</p>
                {oefs.map((ex, i) => (
                  <OefeningKnop key={i} ex={ex} onSelect={onSelect} onClose={onClose} />
                ))}
              </div>
            ))
          ) : (
            // Platte weergave als actief gezocht/gefilterd
            filtered.map((ex, i) => (
              <OefeningKnop key={i} ex={ex} onSelect={onSelect} onClose={onClose} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function OefeningKnop({ ex, onSelect, onClose }) {
  return (
    <button
      onClick={() => { onSelect(ex); onClose(); }}
      data-testid={`exercise-option-${ex.naam?.replace(/\s+/g, "-").toLowerCase()}`}
      className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-[#1e1e1e] transition-colors text-left group"
    >
      <div className="min-w-0">
        <span className="text-white text-sm font-medium group-hover:text-[#C9A84C] transition-colors block truncate">
          {ex.naam}
        </span>
        {(ex.type || ex.apparaat) && (
          <span className="text-zinc-600 text-xs">{[ex.type, ex.apparaat].filter(Boolean).join(" · ")}</span>
        )}
      </div>
      <span className="text-zinc-600 text-xs shrink-0 ml-3">{ex.spiergroep}</span>
    </button>
  );
}
